/**
 * Feature Detail Page — Marketing + Help content for a single feature.
 */

import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { getFeatureBySlug, PLAN_NAMES, FEATURES, type Feature } from '@/config/features';
import {
  ArrowLeft, ArrowRight, Star, Lightbulb, BookOpen, Zap, CheckCircle, ChevronRight,
  Lock, Sparkles, MessageSquare, Upload, Library, Users, BarChart3, Link2,
  Shield, ShieldAlert, Calculator, CalendarDays, FileText, Scale,
  GitCompare, AlertOctagon, CheckCheck, AlertTriangle, FileCheck,
  FileSearch, Building2, Mail, Rocket, HelpCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  MessageSquare, Upload, Library, Users, BarChart3, Link2, Shield,
  ShieldAlert, Calculator, Zap, CalendarDays, FileText, Scale,
  GitCompare, AlertOctagon, CheckCheck, AlertTriangle, FileCheck,
  FileSearch, BookOpen, Building2, Mail, Rocket, Sparkles, HelpCircle, Lock,
};

function getIcon(name: string) {
  return ICON_MAP[name] || Sparkles;
}

// Parse inline markdown links [text](url) into JSX
function renderTextWithLinks(text: string) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      return (
        <Link key={i} to={linkMatch[2]} className="text-[#5048e5] hover:underline font-medium">
          {linkMatch[1]}
        </Link>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export default function FeatureDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { hasPlanTier } = useAuthStore();
  const feature = slug ? getFeatureBySlug(slug) : undefined;

  if (!feature) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-semibold mb-2">Feature not found</h2>
        <Button variant="outline" onClick={() => navigate('/panel/features')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Marketplace
        </Button>
      </div>
    );
  }

  const Icon = getIcon(feature.icon);
  const plan = PLAN_NAMES[feature.minPlan];
  const hasAccess = hasPlanTier(feature.minPlan);
  const relatedFeatures = (feature.relatedSlugs || []).map(s => FEATURES.find(f => f.slug === s)).filter(Boolean) as Feature[];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/panel/features')} className="text-muted-foreground">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to Marketplace
      </Button>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-[#5048e5]/5 via-background to-background p-8">
        <div className="flex items-start gap-5">
          <div className={cn(
            'h-16 w-16 rounded-2xl flex items-center justify-center shrink-0',
            hasAccess ? 'bg-[#5048e5]/10 text-[#5048e5]' : 'bg-muted text-muted-foreground'
          )}>
            <Icon className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">{feature.name}</h1>
              <Badge className={cn('text-xs px-2 py-0.5', plan.bg, plan.color, 'border-0')}>
                {plan.name} Plan
              </Badge>
            </div>
            <p className="text-muted-foreground">{feature.description}</p>
            {feature.highlight && (
              <p className="text-[#5048e5] font-medium mt-2 text-sm italic">"{feature.highlight}"</p>
            )}
            <div className="flex gap-3 mt-4">
              {hasAccess && feature.route ? (
                <Button onClick={() => navigate(feature.route!)} className="bg-[#5048e5] hover:bg-[#443fcc]">
                  Open Feature <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              ) : !hasAccess ? (
                <Button onClick={() => navigate('/panel/upgrade')} className="bg-[#5048e5] hover:bg-[#443fcc]">
                  <Lock className="h-4 w-4 mr-1" /> Upgrade to {plan.name}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        {/* Background decoration */}
        <div className="absolute -right-20 -top-20 w-60 h-60 bg-[#5048e5]/5 rounded-full blur-3xl" />
      </div>

      {/* Why It Matters */}
      <Section
        icon={<Zap className="h-5 w-5 text-amber-500" />}
        title="Why It Matters"
        accent="border-amber-500/30"
      >
        <div className="grid gap-3">
          {feature.whyItMatters.map((point, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="h-6 w-6 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <CheckCircle className="h-3.5 w-3.5 text-amber-600" />
              </div>
              <p className="text-sm leading-relaxed">{renderTextWithLinks(point)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* How to Use */}
      <Section
        icon={<BookOpen className="h-5 w-5 text-[#5048e5]" />}
        title="How to Use"
        accent="border-[#5048e5]/30"
      >
        <ol className="space-y-3">
          {feature.howToUse.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="h-6 w-6 rounded-full bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center shrink-0 text-xs font-bold mt-0.5">
                {i + 1}
              </span>
              <p className="text-sm leading-relaxed">{renderTextWithLinks(step)}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* Pro Tips */}
      <Section
        icon={<Lightbulb className="h-5 w-5 text-green-500" />}
        title="Pro Tips"
        accent="border-green-500/30"
      >
        <div className="grid gap-3">
          {feature.tips.map((tip, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30">
              <Lightbulb className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">{renderTextWithLinks(tip)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA for locked features */}
      {!hasAccess && (
        <div className="rounded-2xl border-2 border-dashed border-[#5048e5]/30 bg-[#5048e5]/5 p-8 text-center">
          <Lock className="h-10 w-10 text-[#5048e5]/40 mx-auto mb-3" />
          <h3 className="text-lg font-semibold mb-1">Unlock {feature.name}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            This feature is available on the {plan.name} plan and above.
          </p>
          <Button onClick={() => navigate('/panel/upgrade')} className="bg-[#5048e5] hover:bg-[#443fcc]">
            Upgrade Now <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Related features */}
      {relatedFeatures.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Related Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {relatedFeatures.map(rf => {
              const RfIcon = getIcon(rf.icon);
              const rfPlan = PLAN_NAMES[rf.minPlan];
              return (
                <Link
                  key={rf.slug}
                  to={`/panel/features/${rf.slug}`}
                  className="flex items-center gap-3 p-3 rounded-lg border hover:border-[#5048e5]/30 hover:shadow-sm transition"
                >
                  <div className="h-9 w-9 rounded-lg bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center shrink-0">
                    <RfIcon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{rf.name}</p>
                    <Badge className={cn('text-[8px] px-1 py-0 mt-0.5', rfPlan.bg, rfPlan.color, 'border-0')}>
                      {rfPlan.name}
                    </Badge>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ icon, title, accent, children }: {
  icon: React.ReactNode;
  title: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('rounded-xl border p-6', accent)}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  );
}
